/**
 * Production-safe logger utility
 * Filters sensitive data and only logs in development
 * For Apple Review compliance - no sensitive data in console
 * I prod: skickar till Sentry via captureException, inte console.
 */

import { captureException } from "@/lib/sentry";

/**
 * Sanitize error to remove sensitive data
 */
function sanitizeError(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message || '';
    // Remove potential sensitive data patterns
    return message
      .replace(/token[=:]\s*[\w-]+/gi, 'token=[REDACTED]')
      .replace(/password[=:]\s*\S+/gi, 'password=[REDACTED]')
      .replace(/email[=:]\s*[\w.@-]+/gi, 'email=[REDACTED]')
      .replace(/api[_-]?key[=:]\s*[\w-]+/gi, 'api_key=[REDACTED]')
      .replace(/authorization[=:]\s*[\w-]+/gi, 'authorization=[REDACTED]');
  }
  return String(error);
}

/**
 * Log error - i DEV: console.error. I prod: Sentry (captureException), ingen console.
 */
export function logError(message: string, error?: unknown): void {
  if (import.meta.env.DEV) {
    console.error(`[Findigo] ${message}`, error);
    return;
  }
  const err = error instanceof Error ? error : new Error(message + (error ? `: ${sanitizeError(error)}` : ""));
  captureException(err, { extra: { message } });
}

/**
 * Log warning - only in development
 */
export function logWarning(message: string, ...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.warn(`[Findigo] ${message}`, ...args);
  }
}

/**
 * Log info - only in development
 */
export function logInfo(message: string, ...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.log(`[Findigo] ${message}`, ...args);
  }
}

/**
 * Log debug - only in development
 */
export function logDebug(message: string, ...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.log(`[Findigo][DEBUG] ${message}`, ...args);
  }
}
